import { createAction } from '@reduxjs/toolkit';
import type {
  GISService,
  GISStateLayer,
  MappedGISIdentity,
} from './gisServicesTypes';

/**
 * Wire contract between gisServicesSlice (ArcGIS-free, imported by apps whose
 * map is Google-only) and gisServicesThunks (which reaches @arcgis/core, ~8.6
 * MiB). The slice must not import the thunks, so it reacts to these
 * declarations instead. gisServicesThunks.test.ts asserts the types match.
 */
export const FETCH_GIS_SERVICES = 'gisServices/fetchGISServices';
export const LOAD_GIS_LAYER = 'gisServices/loadGISLayer';

export interface FetchGISServicesArg {
  orgName: string;
  shouldLoadLayers?: boolean;
}

export interface FetchGISServicesPayload {
  services: GISService[];
  gisIdentities: MappedGISIdentity[];
  layerMap: GISStateLayer[];
  roleName?: string;
  accountId?: string;
}

export interface FetchGISServicesRejection {
  message: string;
  status?: number;
}

export interface LoadGISLayerArg {
  service: GISService;
}

export interface LoadGISLayerPayload {
  layerId: string;
  loadedLayer: GISStateLayer;
}

export interface LoadGISLayerRejection {
  message: string;
  layerId: string;
}

export const fetchGISServicesPending = createAction(
  `${FETCH_GIS_SERVICES}/pending`,
);

export const fetchGISServicesFulfilled = createAction<FetchGISServicesPayload>(
  `${FETCH_GIS_SERVICES}/fulfilled`,
);

export const fetchGISServicesRejected = createAction<
  FetchGISServicesRejection | undefined
>(`${FETCH_GIS_SERVICES}/rejected`);

// The prepare callback is what keeps `action.meta.arg.service` typed in the
// slice's pending reducer.
export const loadGISLayerPending = createAction(
  `${LOAD_GIS_LAYER}/pending`,
  (arg: LoadGISLayerArg) => ({ payload: undefined, meta: { arg } }),
);

export const loadGISLayerFulfilled = createAction<LoadGISLayerPayload>(
  `${LOAD_GIS_LAYER}/fulfilled`,
);

export const loadGISLayerRejected = createAction<
  LoadGISLayerRejection | undefined
>(`${LOAD_GIS_LAYER}/rejected`);
